/**
 * Footer Component
 *
 * A responsive footer that displays copyright information and legal links.
 * This component appears at the bottom of every page in the application and
 * provides essential legal information and copyright notice.
 *
 * Features:
 * - Responsive design that adapts to different screen sizes
 * - Dynamic copyright year that automatically updates
 * - Links to legal pages (Privacy Policy, Terms of Service)
 * - View transitions for smooth navigation to legal pages
 * - Internationalized app name
 */
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router";

/**
 * Footer component for displaying copyright information and legal links
 *
 * This component renders a responsive footer that adapts to different screen sizes.
 * On mobile, it displays the legal links above the copyright notice, while on desktop,
 * it displays them side by side with the copyright on the left and links on the right.
 *
 * @returns A footer component with copyright information and legal links
 */
export default function Footer() {
  const { t } = useTranslation();
  const location = useLocation();
  const pathname = location.pathname;

  return (
    <footer className="text-muted-foreground mt-auto flex items-center justify-between border-t py-3 text-sm md:py-5">
      <div className="mx-auto flex h-full w-full max-w-screen-2xl flex-col items-center justify-between gap-2.5 md:order-none md:flex-row md:gap-0">
        {/* Copyright notice - appears second on mobile, first on desktop */}
        <div className="order-2 md:order-none">
          <p>
            &copy; {new Date().getFullYear()} {t("home.title")}. All rights
            reserved.
          </p>
        </div>

        {/* Links - About + Legal */}
        <div className="order-1 flex gap-10 md:order-none">
          <Link
            to="/about"
            viewTransition
            className={`no-underline ${pathname === "/about" ? "text-foreground font-semibold" : "text-muted-foreground hover:text-foreground hover:font-semibold"}`}
            aria-current={pathname === "/about" ? "page" : undefined}
          >
            {t("about.link")}
          </Link>
          <Link
            to="/legal/privacy-policy"
            viewTransition
            className={`no-underline ${pathname.startsWith("/legal/privacy-policy") ? "text-foreground font-semibold" : "text-muted-foreground hover:text-foreground hover:font-semibold"}`}
            aria-current={
              pathname.startsWith("/legal/privacy-policy") ? "page" : undefined
            }
          >
            {t("legal.privacyPolicy")}
          </Link>
          <Link
            to="/legal/terms-of-service"
            viewTransition
            className={`no-underline ${pathname.startsWith("/legal/terms-of-service") ? "text-foreground font-semibold" : "text-muted-foreground hover:text-foreground hover:font-semibold"}`}
            aria-current={
              pathname.startsWith("/legal/terms-of-service")
                ? "page"
                : undefined
            }
          >
            {t("legal.termsOfService")}
          </Link>
          <Link
            to="/legal/cookie-policy"
            viewTransition
            className={`no-underline ${pathname.startsWith("/legal/cookie-policy") ? "text-foreground font-semibold" : "text-muted-foreground hover:text-foreground hover:font-semibold"}`}
            aria-current={
              pathname.startsWith("/legal/cookie-policy") ? "page" : undefined
            }
          >
            {t("legal.cookiePolicy")}
          </Link>
          <Link
            to="/legal/youth-policy"
            viewTransition
            className={`no-underline ${pathname.startsWith("/legal/youth-policy") ? "text-foreground font-semibold" : "text-muted-foreground hover:text-foreground hover:font-semibold"}`}
            aria-current={
              pathname.startsWith("/legal/youth-policy") ? "page" : undefined
            }
          >
            {t("legal.youthPolicy")}
          </Link>
        </div>
      </div>
    </footer>
  );
}
